import { DestroyRef, effect, inject, Injectable, Injector, runInInjectionContext, untracked } from '@angular/core';
import { CityStoreFacade } from '@lentau/city/store/city.facade';
import { PickupStore, UserAddress } from '@lentau/address/shared/interfaces';
import { MissionAddressService } from '@lentau/address/mission-address/application/mission-address.service';
import { filter, take } from 'rxjs';
import { AuthFacade } from '@lentau/auth/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeliveryModeFacade } from '@lentau/delivery-mode/store/delivery-mode.facade';

/**
 * Cities list on session; region/user once after mission address settles.
 */
@Injectable({ providedIn: 'root' })
export class CityRequestService {
  private cityStoreFacade: CityStoreFacade = inject(CityStoreFacade);
  private missionAddressService: MissionAddressService = inject(MissionAddressService);
  private deliveryModeFacade: DeliveryModeFacade = inject(DeliveryModeFacade);
  private authFacade: AuthFacade = inject(AuthFacade);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private injector: Injector = inject(Injector);
  private regionByAddressSubscribed: boolean = false;

  public getCitiesListAndRegion(): void {
    this.authFacade.sessionToken$
      .pipe(
        filter((token: string) => !!token?.length),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.cityStoreFacade.getCitiesList();
      });
  }

  public getUserRegionByAddress(): void {
    if (this.regionByAddressSubscribed) {
      return;
    }

    this.regionByAddressSubscribed = true;

    runInInjectionContext(this.injector, () => {
      effect(() => {
        const sessionReady: boolean = !!this.authFacade.sessionToken()?.length;
        const missionAddress: UserAddress | PickupStore = this.missionAddressService.missionAddress();
        const addressLoading: boolean = this.missionAddressService.isAddressDataLoading();
        const deliveryReady: boolean = this.deliveryModeFacade.isLoaded();

        // Cookie stub already has missionAddress — wait for delivery settle, not a timer.
        if (!sessionReady || !missionAddress || addressLoading || !deliveryReady) {
          return;
        }

        untracked(() => {
          const pickupRegionId: number | undefined =
            'regionId' in missionAddress ? (missionAddress as PickupStore).regionId : undefined;
          this.cityStoreFacade.getUserRegion(pickupRegionId);
        });
      });
    });
  }
}
